import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";

// Connects to data-controller="direct-upload"
export default class extends Controller {
  static targets = ["fileTemplate", "fileInputTemplate", "input", "image", "imageResults", "label", "submitButton", "showMoreLink"];

  static values = {
    allowedMimeTypes: Array,
    maxFileSize: Number,
    maxFilesCount: Number,
    errorMessage: String,
    invalidFileTypeMessage: String,
    maxFileSizeExceededMessage: String,
  };

  initialize() {
    this.directUploadUrl = this.inputTarget.dataset.directUploadUrl; // Set by direct upload
    this.baseSrcUrl = "/rails/active_storage/blobs/redirect";
    this.errorTimeout;
    this.maxFileSizeInMegabytes = this.maxFileSizeValue / 1024 / 1024;
    this.fileElements = Array.from(document.querySelectorAll(".file--template")); // We keep track of the file elements to remove them later
  }

  validateFile(file) {
    if (!this.allowedMimeTypesValue.includes(file.type)) {
      console.error(`Invalid file type: ${file.type}`);
      window.dispatchEvent(new CustomEvent("flash:create", { detail: { type: "danger", message: this.invalidFileTypeMessageValue } }));
      return false;
    }

    if (file.size > this.maxFileSizeValue) {
      console.error(`File size exceeds the limit of ${this.maxFileSizeInMegabytes} MB`);
      window.dispatchEvent(new CustomEvent("flash:create", { detail: { type: "danger", message: this.maxFileSizeExceededMessageValue } }));

      return false;
    }

    return true;
  }

  directUploadFile(file) {
    const upload = new DirectUpload(file, this.directUploadUrl, this);
    this.submitButtonTarget.disabled = true;

    return new Promise((resolve, reject) => {
      upload.create((error, blob) => {
        if (error) {
          console.error(error);
          this.displayState("error");

          window.dispatchEvent(new CustomEvent("flash:create", { detail: { type: "danger", message: this.errorMessageValue } }));

          const index = this.fileElements.findIndex((el) => el === upload.fileElement);
          if (index !== -1) {
            this.fileElements[index].remove();
            this.fileElements.splice(index, 1);
          }

          reject(error);
          return;
        }

        this.createHiddenInput(blob.signed_id);

        const fileElement = event.currentTarget.upload.fileElement;

        fileElement.querySelector(".remove--button").dataset.fileSignedId = blob.signed_id;

        if (blob.content_type != "application/pdf") {
          fileElement.querySelector("img").src = `${this.baseSrcUrl}/${blob.signed_id}/${blob.filename}`;
        }

        resolve(upload);
      });
    });
  }

  createHiddenInput(signedId) {
    const hiddenField = this.fileInputTemplateTarget.content.cloneNode(true);
    const input = hiddenField.querySelector("input");

    input.value = signedId;
    input.disabled = false;

    this.element.appendChild(hiddenField);
  }

  changed(validate = true) {
    Promise.all(
      Array.from(this.inputTarget.files).map((file) => {
        if (validate && !this.validateFile(file)) return Promise.resolve();

        return this.directUploadFile(file);
      })
    ).then(() => {
      this.submitButtonTarget.disabled = false; // Re-enable the submit button
    });
  }

  removeUpload(event) {
    const fileElement = event.currentTarget.closest(".file--template");
    fileElement.remove();

    // NOTE: Only removes them visually, they are uploaded already (direct upload)
    // They will be removed by a background job if the blob is not attached to a record.

    // We remove the file from the input element, so that when saving a new record we don't associate the removed file with the record
    const fileNameToRemove = event.currentTarget.dataset.fileName;
    const files = Array.from(this.inputTarget.files).filter((file) => file.name !== fileNameToRemove);
    const dataTransfer = new DataTransfer();

    files.forEach((file) => dataTransfer.items.add(file));

    this.inputTarget.files = dataTransfer.files;
    this.fileElements = this.fileElements.filter((file) => file !== fileElement);

    const inputToRemove = document.querySelector(`[value="${event.currentTarget.dataset.fileSignedId}"]`);

    if (inputToRemove) {
      const inputName = inputToRemove.name;
      const inputsWithSameName = document.querySelectorAll(`input[name="${inputName}"]`);

      if (inputsWithSameName.length === 1) {
        inputToRemove.value = ""; // Removing the input would not cause it to be removed from the backend
      } else {
        inputToRemove.remove();
      }
    }
  }

  directUploadWillStoreFileWithXHR({ upload }) {
    // This function gets called by DirectUpload before it starts the XHR request

    const fileElement = this.fileTemplateTarget.cloneNode(true).content;
    upload.fileElement = fileElement.querySelector(".file--template");

    fileElement.querySelector(".file--name").textContent = event.currentTarget.response.filename;
    fileElement.querySelector(".file--size").textContent = `${Math.floor(event.currentTarget.response.byte_size / 1024)} KB`;

    const removeButton = fileElement.querySelector(".remove--button");
    removeButton.dataset.fileName = event.currentTarget.response.filename;

    const xhrRequest = event.currentTarget;

    // We attempt to abort the request, this also triggers the removeUpload function
    removeButton.addEventListener(
      "click",
      () => {
        xhrRequest.abort();
      },
      {
        once: true,
      }
    );

    upload.progressWrapper = fileElement.querySelector(".progress--wrapper");
    upload.progressBar = fileElement.querySelector(".progress--bar");

    this.imageResultsTarget.appendChild(fileElement);

    this.imageResultsTarget.parentElement.scrollTo({
      top: this.imageResultsTarget.scrollHeight,
      behavior: "smooth",
    });

    upload.addEventListener("error", () => {
      upload.progressBar.classList.add("bg-danger");
      window.dispatchEvent(new CustomEvent("flash:create", { detail: { type: "danger", message: this.errorMessageValue } }));
    });
    upload.addEventListener("timeout", () => {
      upload.progressBar.classList.add("bg-danger");
    });
    upload.addEventListener("loadend", () => {
      upload.progressBar.classList.add("bg-success");
    });
    upload.addEventListener("progress", (event) => {
      const percentage = Math.round((event.loaded / event.total) * 100);
      upload.progressBar.style.width = `${percentage}%`;
    });

    this.fileElements.push(upload.fileElement);
    this.showMoreLinkTarget.classList.toggle("d-none", this.fileElements.length <= 2);
  }

  dragEnter() {
    this.labelTarget.classList.add("dragover");
  }

  dragLeave() {
    this.labelTarget.classList.remove("dragover");
  }

  displayState(state) {
    this.labelTarget.classList.add(`dragover--${state}`);
    this.errorTimeout = setTimeout(() => {
      this.labelTarget.classList.remove(`dragover--${state}`);
    }, 1000);
  }

  drop(event) {
    event.preventDefault();

    clearTimeout(this.errorTimeout);

    const filesArray = Array.from(event.dataTransfer.files);
    const files = filesArray.filter((file) => this.validateFile(file));

    if (files.length > 0 && files.length < filesArray.length) {
      // Incase some files were correct but not all
      this.displayState("warning");
    } else if (files.length === 0) {
      this.displayState("error");
    } else {
      this.displayState("success");
    }

    const dataTransfer = new DataTransfer();

    Promise.all(
      files.map((file) => {
        dataTransfer.items.add(file);
        return this.directUploadFile(file);
      })
    ).then(() => {
      this.inputTarget.files = dataTransfer.files;

      this.labelTarget.classList.remove("dragover");
      this.submitButtonTarget.disabled = false;
    });
  }
}
